/**
 * Map KMZ to Areas (AST-87, section 4)
 *
 * Three sections — Structures, Project/Laydown Yards, Other. Each lets the
 * user add folders (every placemark beneath, including nested folders) or
 * single placemarks from the parsed KMZ/KML tree. A node can belong to one
 * section only; picking it in one removes it from the others' pickers.
 * Mapping is optional — zero elements is a valid save.
 *
 * Controlled: the parent owns `selections` (so the wizard keeps them across
 * Back/Next) and decides when to save. Used by ProjectSetupWizard (step 3) and
 * MapAreasDialog (reopen from the project page).
 */

import React, { useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { Close as RemoveIcon, Folder as FolderIcon, Place as PlaceIcon, History as SavedIcon } from '@mui/icons-material';
import { SECTIONS, availableNodes, selectionCount } from '../../utils/areaElements';

export const emptySelections = () => ({ structures: [], yards: [], other: [] });

const iconFor = (kind) => (kind === 'folder' ? <FolderIcon fontSize="small" /> : kind === 'saved' ? <SavedIcon fontSize="small" /> : <PlaceIcon fontSize="small" />);

const labelFor = (sel, index) => {
  if (sel.kind === 'saved') return sel.label;
  if (sel.kind === 'folder') return index?.folders.get(sel.nodeId)?.path || 'Folder';
  const pm = index?.placemarks.get(sel.nodeId);
  return pm ? pm.name : 'Placemark';
};

const SectionPicker = ({ section, selections, index, onChange, disabled }) => {
  const [input, setInput] = useState('');
  const options = useMemo(
    () => (index ? availableNodes(section.key, selections, index) : []),
    [section.key, selections, index]
  );
  const current = selections[section.key] || [];
  const total = current.reduce((n, s) => n + selectionCount(s, index), 0);

  const add = (opt) => {
    if (!opt) return;
    onChange({ ...selections, [section.key]: [...current, { kind: opt.kind, nodeId: opt.nodeId }] });
    setInput('');
  };
  const remove = (nodeId) => {
    onChange({ ...selections, [section.key]: current.filter((s) => s.nodeId !== nodeId) });
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={600} component="h3">{section.label}</Typography>
        <Chip size="small" label={`${total} placemark${total === 1 ? '' : 's'}`} />
      </Box>

      <Autocomplete
        size="small"
        options={options}
        value={null}
        inputValue={input}
        onInputChange={(_e, v) => setInput(v)}
        onChange={(_e, opt) => add(opt)}
        disabled={disabled || !index}
        getOptionLabel={(o) => o.label}
        isOptionEqualToValue={(o, v) => o.nodeId === v.nodeId}
        groupBy={(o) => (o.kind === 'folder' ? 'Folders' : 'Placemarks')}
        renderOption={(props, o) => (
          <li {...props} key={o.nodeId}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              {iconFor(o.kind)}
              <Typography variant="body2" sx={{ flexGrow: 1 }}>{o.label}</Typography>
              {o.kind === 'folder' && <Typography variant="caption" color="text.secondary">{o.count} placemarks</Typography>}
            </Box>
          </li>
        )}
        noOptionsText={index ? 'Nothing left to add' : 'Upload a KMZ/KML file to add elements'}
        renderInput={(params) => (
          <TextField
            {...params}
            label={`Add to ${section.label}`}
            placeholder={index ? 'Search folders and placemarks' : 'No file loaded'}
          />
        )}
      />

      {current.length > 0 ? (
        <List dense disablePadding sx={{ mt: 1 }}>
          {current.map((sel) => {
            const count = selectionCount(sel, index);
            const label = labelFor(sel, index);
            return (
              <ListItem
                key={sel.nodeId}
                disableGutters
                secondaryAction={(
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={() => remove(sel.nodeId)}
                    disabled={disabled}
                    aria-label={`Remove ${label} from ${section.label}`}
                  >
                    <RemoveIcon fontSize="small" />
                  </IconButton>
                )}
              >
                <Box sx={{ mr: 1, display: 'flex', color: 'text.secondary' }}>{iconFor(sel.kind)}</Box>
                <ListItemText
                  primary={sel.kind === 'placemark' ? label : `${label} (${count} placemark${count === 1 ? '' : 's'})`}
                  secondary={sel.kind === 'saved' ? 'Saved' : null}
                />
              </ListItem>
            );
          })}
        </List>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Nothing mapped. Counts fall back to the project.
        </Typography>
      )}
    </Paper>
  );
};

const MapKmzToAreas = ({ index, selections, onChange, disabled = false, fileName, skipped = 0 }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    <Typography variant="body2" color="text.secondary">
      Add folders or single placemarks to each section. A folder adds every placemark inside it.
      Each element can be in one section only. Mapping is optional.
    </Typography>

    {fileName && (
      <Typography variant="caption" color="text.secondary">
        From {fileName}
        {index ? ` · ${index.placemarks.size} placemarks` : ''}
      </Typography>
    )}
    {skipped > 0 && (
      <Alert severity="info">
        {`${skipped} placemark${skipped === 1 ? ' has' : 's have'} no coordinates and can't be mapped.`}
      </Alert>
    )}

    {SECTIONS.map((section) => (
      <SectionPicker
        key={section.key}
        section={section}
        selections={selections}
        index={index}
        onChange={onChange}
        disabled={disabled}
      />
    ))}
  </Box>
);

export default MapKmzToAreas;
